import { t } from "../i18n/index.ts";
import { following, uploading } from "../lib/imports.ts";
import { location } from "../lib/router.ts";
import { Link } from "../ui/Button.tsx";
import { CheckIcon, ImportingIcon, IssuesIcon, UploadIcon } from "../ui/icons.tsx";

/** A whole, in percent. */
const ALL = 100;

const percent = (done: number, total: number) => Math.min(ALL, Math.floor((done / Math.max(total, 1)) * ALL));

/** The page's path, without its query. */
const pathOf = (page: string) => page.split("?")[0] ?? page;

/**
 * Anywhere in the app, while an export uploads or an import is applied (or just finished): a small chip that says how
 * it's going and leads back to its page. Not on that page, which says it all.
 */
export function ImportChip() {
  const u = uploading.value;
  const f = following.value;
  const here = location.value.pathname;
  let text: string;
  let page: string;
  let icon = <UploadIcon size={16} />;
  if (u) {
    page = u.page;
    text =
      u.phase === "failed"
        ? t("import.chip.uploadFailed")
        : u.phase === "waiting"
          ? t("import.chip.waiting")
          : u.phase === "reading"
            ? t("import.reading")
            : t("import.chip.uploading", { percent: percent(u.sent, u.file.size) });
    if (u.phase === "failed" || u.phase === "waiting") {
      icon = <IssuesIcon size={16} />;
    }
  } else if (f) {
    page = f.page;
    const s = f.status;
    if (s?.state === "done") {
      text = t("import.chip.done");
      icon = <CheckIcon size={16} />;
    } else if (s?.state === "failed") {
      text = t("import.chip.failed");
      icon = <IssuesIcon size={16} />;
    } else {
      text = t("import.chip.applying");
      icon = <ImportingIcon size={16} />;
    }
  } else {
    return null;
  }
  if (pathOf(page) === here) {
    return null;
  }
  return (
    <div role="status" class="fixed right-4 bottom-4 z-30 max-md:bottom-20">
      <Link
        href={page}
        class="flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-800 shadow-md hover:border-zinc-300"
      >
        {icon}
        {text}
      </Link>
    </div>
  );
}
